import { useEffect, useState } from 'react';
import type { PhaseMetrics } from '@/types/dashboard';
import { cn, formatPercentage, getMetricColor } from '@/lib/utils';

interface ConfusionMatrixProps {
  phase: PhaseMetrics;
  title: string;
  candidateCount: number;
  isMultiRun?: boolean;
}

function AnimatedNumber({ value, duration = 600 }: { value: number; duration?: number }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (value === 0) {
      setDisplay(0);
      return;
    }

    const startTime = performance.now();
    const startValue = 0;

    function animate(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(startValue + (value - startValue) * eased));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    }

    requestAnimationFrame(animate);
  }, [value, duration]);

  return <>{display}</>;
}

export function ConfusionMatrix({ phase, title, candidateCount, isMultiRun }: ConfusionMatrixProps) {
  const cells = [
    {
      label: 'TP',
      description: 'True Positive',
      value: phase.tp,
      color: 'bg-green-500/20 border-green-500/40 text-green-400',
      hoverColor: 'hover:bg-green-500/30',
    },
    {
      label: 'FP',
      description: 'False Positive',
      value: phase.fp,
      color: 'bg-red-500/20 border-red-500/40 text-red-400',
      hoverColor: 'hover:bg-red-500/30',
    },
    {
      label: 'FN',
      description: 'False Negative',
      value: phase.fn,
      color: 'bg-red-500/20 border-red-500/40 text-red-400',
      hoverColor: 'hover:bg-red-500/30',
    },
    {
      label: 'TN',
      description: 'True Negative',
      value: phase.tn,
      color: 'bg-green-500/20 border-green-500/40 text-green-400',
      hoverColor: 'hover:bg-green-500/30',
    },
  ];

  const metrics = [
    { label: 'Precision', value: phase.precision, stddev: phase.precision_stddev },
    { label: 'Recall', value: phase.recall, stddev: phase.recall_stddev },
    { label: 'F1', value: phase.f1, stddev: phase.f1_stddev },
    { label: 'Specificity', value: phase.specificity, stddev: phase.specificity_stddev },
  ];

  return (
    <div className="rounded-xl border border-primary/20 bg-card/50 p-5 backdrop-blur-xs">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold bg-linear-to-r from-primary to-orange-400 bg-clip-text text-transparent">
          {title}
        </h3>
        <span className="text-xs font-mono text-muted-foreground bg-primary/10 px-2 py-1 rounded">
          {candidateCount} candidates
        </span>
      </div>

      {/* Matrix Grid */}
      <div className="relative">
        {/* Column Headers */}
        <div className="grid grid-cols-[80px_1fr_1fr] gap-2 mb-2">
          <div />
          <div className="text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Predicted YES
          </div>
          <div className="text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Predicted NO
          </div>
        </div>

        {/* Row 1: Actual YES */}
        <div className="grid grid-cols-[80px_1fr_1fr] gap-2 mb-2">
          <div className="flex items-center justify-end pr-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider writing-mode-vertical">
              Actual YES
            </span>
          </div>
          {cells.slice(0, 2).map((cell) => (
            <div
              key={cell.label}
              className={cn(
                'rounded-lg border p-4 text-center transition-all duration-200',
                cell.color,
                cell.hoverColor
              )}
            >
              <div className="text-3xl font-bold tabular-nums">
                <AnimatedNumber value={cell.value} />
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-widest mt-1 opacity-70">
                {cell.label}
              </div>
              <div className="text-[10px] mt-0.5 opacity-50">
                {cell.description}
              </div>
            </div>
          ))}
        </div>

        {/* Row 2: Actual NO */}
        <div className="grid grid-cols-[80px_1fr_1fr] gap-2">
          <div className="flex items-center justify-end pr-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Actual NO
            </span>
          </div>
          {cells.slice(2, 4).map((cell) => (
            <div
              key={cell.label}
              className={cn(
                'rounded-lg border p-4 text-center transition-all duration-200',
                cell.color,
                cell.hoverColor
              )}
            >
              <div className="text-3xl font-bold tabular-nums">
                <AnimatedNumber value={cell.value} />
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-widest mt-1 opacity-70">
                {cell.label}
              </div>
              <div className="text-[10px] mt-0.5 opacity-50">
                {cell.description}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-primary/10">
        {metrics.map((metric) => {
          const pct = metric.value;
          return (
            <div key={metric.label} className="text-center">
              <div className={cn('text-lg font-bold tabular-nums', getMetricColor(pct))}>
                {formatPercentage(pct)}
                {isMultiRun && metric.stddev != null && metric.stddev > 0 && (
                  <span className="text-xs font-normal text-muted-foreground/60 ml-0.5">
                    &plusmn;{metric.stddev.toFixed(1)}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                {metric.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
